import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { AdaptiveOverlay, useSheetPlacement } from './adaptive-overlay';
import { KitButton } from './button';
import type { FieldControlProps } from './form-field';
import { checkedOptions, useComboOptions } from './combobox-options';

export interface ComboOption {
  id: string;
  label: string;
  disabled?: boolean;
}
export interface ComboboxProps extends FieldControlProps {
  label: string;
  locale: string;
  readOnly?: boolean;
  multiple?: boolean;
  options?: readonly ComboOption[];
  selectedOptions?: readonly ComboOption[];
  /** Consumer uses its admitted SDK; this callback is no source grant. */
  loadOptions?(query: string, input: { signal: AbortSignal }): Promise<readonly ComboOption[]>;
  copy: {
    close: string;
    loading: string;
    failed: string;
    unknown: string;
    noMatches(query: string): string;
    remove(label: string): string;
    more(count: number): string;
  };
}

function selectedIds(value: unknown, multiple: boolean) {
  if (multiple)
    return Array.isArray(value)
      ? [...new Set(value.filter((id): id is string => typeof id === 'string'))]
      : [];
  return typeof value === 'string' ? [value] : [];
}
function nextIndex(options: readonly ComboOption[], active: number, direction: number) {
  for (let step = 1; step <= options.length; step++) {
    const index = (active + step * direction + options.length) % options.length;
    if (!options[index].disabled) return index;
  }
  return -1;
}

export function Combobox(props: ComboboxProps) {
  const { id, label, copy, multiple = false, disabled, readOnly } = props;
  const [opening, setOpening] = useState({ open: false, epoch: 0 });
  const open = opening.open;
  const setOpen = (visible: boolean) =>
    setOpening((current) => ({
      open: visible,
      epoch: visible && !current.open ? current.epoch + 1 : current.epoch,
    }));
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);
  const listId = `${id}-${useId().replaceAll(':', '')}-options`;
  const result = useComboOptions(props, query, open, opening.epoch);
  const ids = selectedIds(props.value, multiple);
  const known = [
    ...checkedOptions(props.options ?? []),
    ...checkedOptions(props.selectedOptions ?? []),
    ...result.options,
  ];
  const selected = ids.map((selectedId) => ({
    id: selectedId,
    label: known.find((option) => option.id === selectedId)?.label ?? copy.unknown,
  }));
  const blocked = disabled || readOnly;
  const sheet = useSheetPlacement();
  const visible = open && !blocked;
  useEffect(() => {
    if (visible && active < 0 && result.status === 'ready')
      setActive(nextIndex(result.options, -1, 1));
  }, [visible, active, result.status, result.options]);
  const choose = (option: ComboOption) => {
    if (blocked || option.disabled || result.status !== 'ready') return;
    props.onValueChange(
      multiple
        ? ids.includes(option.id)
          ? ids.filter((value) => value !== option.id)
          : [...ids, option.id]
        : option.id,
    );
    if (!multiple) {
      setOpen(false);
      setQuery('');
      props.onBlur();
    }
  };
  const search = (text: string) => {
    setQuery(text);
    setActive(-1);
    setOpen(true);
  };
  const keyboard = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
      // Keep composition commands away from the overlay's document shortcuts.
      event.stopPropagation();
      return;
    }
    if (blocked) return;
    if (event.key === 'Escape') {
      setOpen(false);
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter' && visible) {
      event.preventDefault();
      if (result.options[active]) choose(result.options[active]);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    setOpen(true);
    const origin = event.key === 'Home' ? -1 : event.key === 'End' ? 0 : active;
    setActive(nextIndex(result.options, origin, ['ArrowUp', 'End'].includes(event.key) ? -1 : 1));
  };
  const input = (inSheet: boolean) => (
    <input
      id={inSheet ? `${id}-search` : id}
      className="pc-field-input"
      role="combobox"
      aria-label={label}
      aria-expanded={visible}
      aria-controls={listId}
      aria-autocomplete="list"
      aria-activedescendant={visible && result.options[active] ? `${listId}-${active}` : undefined}
      aria-invalid={props['aria-invalid']}
      aria-describedby={props['aria-describedby']}
      disabled={disabled}
      readOnly={readOnly}
      value={visible ? query : multiple ? '' : (selected[0]?.label ?? '')}
      onFocus={() => {
        if (!blocked) setOpen(true);
      }}
      onChange={(event) => {
        if (!blocked) search(event.currentTarget.value);
      }}
      onBlur={props.onBlur}
      onKeyDown={keyboard}
    />
  );
  return (
    <span
      className="pc-field pc-field-md"
      data-kind="combobox"
      data-state={disabled ? 'disabled' : readOnly ? 'readonly' : undefined}
    >
      {multiple &&
        selected.slice(0, 3).map((option) => (
          <span key={option.id} className="pc-token">
            {option.label}
            <KitButton
              variant="quiet"
              disabled={blocked}
              aria-label={copy.remove(option.label)}
              onClick={() => props.onValueChange(ids.filter((value) => value !== option.id))}
            >
              ×
            </KitButton>
          </span>
        ))}
      {multiple && selected.length > 3 && (
        <span className="pc-token">{copy.more(selected.length - 3)}</span>
      )}
      <AdaptiveOverlay
        open={visible}
        onOpenChange={setOpen}
        disabled={blocked}
        title={label}
        closeLabel={copy.close}
        anchorOnly
        keepAnchorFocus={!sheet}
        trigger={input(false)}
      >
        {sheet && input(true)}
        <div
          className="pc-listbox"
          id={listId}
          role="listbox"
          aria-label={label}
          aria-multiselectable={multiple || undefined}
          aria-busy={result.status === 'loading'}
        >
          {result.options.map((option, index) => (
            <div
              key={option.id}
              id={`${listId}-${index}`}
              className="pc-option"
              role="option"
              aria-selected={ids.includes(option.id)}
              aria-disabled={option.disabled || undefined}
              data-active={active === index ? '' : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
            >
              {ids.includes(option.id) && <span aria-hidden="true">✓ </span>}
              {option.label}
            </div>
          ))}
          {result.status !== 'ready' && (
            <div className="pc-option" role="status">
              {result.status === 'loading' ? copy.loading : copy.failed}
            </div>
          )}
          {result.status === 'ready' && !result.options.length && (
            <div className="pc-option" role="status">
              {copy.noMatches(query)}
            </div>
          )}
        </div>
      </AdaptiveOverlay>
    </span>
  );
}
